import React from 'react';
import { Icon } from '../Icon/Icon';
import { ViewportOverlay } from '../Overlay/ViewportOverlay';
import type { ExtractedRule } from '../../settings-channels/types';

export interface RecepAnalyzeModalProps {
  open: boolean;
  rules: ExtractedRule[];
  onClose: () => void;
  onDelete: (id: string) => void;
}

/**
 * What ELVA found in your rules (spec 15 §9.5).
 *
 * Shown after `Analyze the text`, on both the emergency card and the house-rules
 * card. It lists **only what needs a decision** — a modal that repeated all six
 * would be a second copy of the page behind it. When every sentence is in force
 * it says so in one line and offers `Done`, nothing else.
 */
export function RecepAnalyzeModal({ open, rules, onClose, onDelete }: RecepAnalyzeModalProps) {
  // Every number is computed from the rules themselves (§3.1).
  const inForce = rules.filter((r) => r.state === 'in_force').length;
  const conflicts = rules.filter((r) => r.state === 'conflict');
  const notApplied = rules.filter((r) => r.state === 'not_applied');
  const needsDecision = [...conflicts, ...notApplied];

  return (
    <ViewportOverlay open={open} onClose={onClose}>
      <div
        onClick={(e) => { e.stopPropagation(); }}
        className="flex max-h-[90vh] w-[min(660px,calc(100vw-32px))] flex-col overflow-hidden rounded-[18px] border border-[var(--border-default)] bg-[var(--surface-card)] shadow-[0_18px_48px_rgba(36,41,54,.18)]"
      >
        <div className="flex items-start justify-between gap-3 border-b border-[var(--border-default)] px-6 py-5">
          <div>
            <h2 className="m-0 text-[17px] font-bold tracking-[-0.02em] text-[var(--text-primary)]">
              What ELVA found in your rules
            </h2>
            <p className="m-0 mt-1 text-[12px] leading-[1.6] text-[var(--text-muted)]">
              {rules.length} {rules.length === 1 ? 'sentence' : 'sentences'} read.{' '}
              {needsDecision.length === 0 ? (
                <strong className="text-[var(--text-secondary)]">Every one is in force — nothing needs you.</strong>
              ) : (
                <>
                  <strong className="text-[var(--text-secondary)]">
                    {needsDecision.length} need{needsDecision.length === 1 ? 's' : ''} a decision from you
                  </strong>{' '}
                  — the rest are in force and nothing else is needed.
                </>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] border border-[var(--border-default)] text-[var(--text-muted)] hover:bg-[var(--surface-sunken)]"
          >
            <Icon kind="x" size={14} stroke={2} />
          </button>
        </div>

        {/* the tally */}
        <div className="grid grid-cols-2 border-b border-[var(--border-default)] sm:grid-cols-4">
          <Tally value={rules.length} label="sentences read" />
          <Tally value={inForce} label="in force" colour="var(--money-green)" />
          <Tally value={conflicts.length} label="in conflict" colour="var(--warn-amber)" />
          <Tally value={notApplied.length} label="could not be applied" colour="var(--alert-red)" last />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {needsDecision.map((r) => (
            <div
              key={r.id}
              className="flex gap-4 px-6 py-5"
              style={{ borderBottom: '1px solid #edf0f6' }}
            >
              <span
                aria-hidden
                className="w-[3px] shrink-0 self-stretch rounded-full"
                style={{ background: r.state === 'conflict' ? 'var(--warn-amber)' : 'var(--alert-red)' }}
              />
              <div className="min-w-0 flex-1">
                <span
                  className="inline-flex rounded-full px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.06em]"
                  style={
                    r.state === 'conflict'
                      ? { background: '#FBF3E4', color: 'var(--warn-amber)' }
                      : { background: '#FBEDEC', color: 'var(--alert-red)' }
                  }
                >
                  {r.state === 'conflict' ? 'Conflict' : 'Could not be applied'}
                </span>
                <div className="mt-2 text-[14px] font-bold text-[var(--text-primary)]">&ldquo;{r.quote}&rdquo;</div>
                <p className="m-0 mt-1.5 text-[12.5px] leading-[1.65] text-[var(--text-muted)]">{r.effect}</p>
                {r.note ? (
                  <p className="m-0 mt-3 rounded-[10px] border border-[var(--border-default)] px-3.5 py-3 text-[12.5px] leading-[1.65] text-[var(--text-secondary)]">
                    {r.resolution ? (
                      <>
                        <strong>One sentence resolves it:</strong> <em>&ldquo;{r.resolution}&rdquo;</em> Delete this
                        rule, then edit that one.
                      </>
                    ) : (
                      r.note
                    )}
                  </p>
                ) : null}
                <button
                  type="button"
                  onClick={() => { onDelete(r.id); }}
                  className="mt-3 rounded-[9px] border border-[var(--alert-red-line,#F0D2D0)] px-3.5 py-2 text-[12.5px] font-semibold text-[var(--alert-red)] hover:bg-[#FBEDEC]"
                >
                  Delete this rule
                </button>
              </div>
            </div>
          ))}

          {inForce > 0 ? (
            <p className="m-0 px-6 py-4 text-[12.5px] leading-[1.65] text-[var(--text-secondary)]">
              <strong>
                The other {inForce} {inForce === 1 ? 'is' : 'are'} in force.
              </strong>{' '}
              Each one is on the page behind this, with what ELVA will actually do about it. Two of them repeat
              something already set elsewhere — their cards say where.
            </p>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-4 border-t border-[var(--border-default)] bg-[var(--surface-sunken)] px-6 py-4">
          <span className="text-[11.5px] leading-[1.6] text-[var(--text-muted)]">
            Nothing is saved until you close this.{' '}
            <strong className="text-[var(--text-secondary)]">
              Deleting here removes the sentence from the box too.
            </strong>
          </span>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-[10px] bg-[var(--brand-indigo)] px-5 py-2 text-[13.5px] font-semibold text-white"
          >
            Done
          </button>
        </div>
      </div>
    </ViewportOverlay>
  );
}

function Tally({
  value,
  label,
  colour,
  last,
}: {
  value: number;
  label: string;
  colour?: string;
  last?: boolean;
}) {
  return (
    <div
      className="px-5 py-4"
      style={last ? undefined : { borderRight: '1px solid var(--border-default)' }}
    >
      <div className="text-[24px] font-bold leading-none" style={{ color: colour ?? 'var(--text-primary)' }}>
        {value}
      </div>
      <div className="mt-1.5 text-[11.5px] text-[var(--text-muted)]">{label}</div>
    </div>
  );
}
